import { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, Loader2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { SEO, pageSEO } from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const contactInfo = [
  { icon: MapPin, title: 'Our Location', details: ['Gbarnga City, Bong County', 'Liberia, West Africa'] },
  { icon: Phone, title: 'Phone', details: ['+231 886 608 842'] },
  { icon: Mail, title: 'Email', details: ['info@cepres.edu.lr', 'admissions@cepres.edu.lr'] },
  { icon: Clock, title: 'Office Hours', details: ['Monday - Friday: 8:00 AM - 5:00 PM', 'Saturday: 9:00 AM - 1:00 PM'] },
];

export default function Contact() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    subject: '',
    message: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully!', { description: 'We will get back to you soon.' });
      setFormData({ full_name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to send message', { description: 'Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen pt-20">
      <SEO {...pageSEO.contact} url="/contact" />
      {/* Hero Section */}
      <section className="relative py-24 bg-[#0D4A2E] pattern-overlay">
        <div className="relative max-w-7xl mx-auto px-4 lg:px-8">
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-4">
            Contact <span className="text-[#C5A028]">Us</span>
          </h1>
          <p className="text-white/70 text-xl max-w-2xl">
            Have questions? We'd love to hear from you. Reach out to our team.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section data-testid="contact-info-section" className="py-12 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0">
            {contactInfo.map((info, index) => (
              <div
                key={info.title}
                data-testid={`contact-info-${info.title.toLowerCase().replace(' ', '-')}`}
                className="bg-white p-6 border border-[#E5E5E5] hover:border-[#C5A028] transition-colors duration-300"
              >
                <info.icon className="w-8 h-8 text-[#C5A028] mb-4" />
                <h3 className="text-lg font-bold text-[#0D4A2E] mb-2">{info.title}</h3>
                {info.details.map((detail, i) => (
                  <p key={i} className="text-[#666666] text-sm">{detail}</p>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section data-testid="contact-form-section" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Form */}
            <div>
              <h2 className="text-3xl font-bold text-[#0D4A2E] mb-2">Send Us a Message</h2>
              <p className="text-[#666666] mb-8">Fill out the form and we'll respond within 24-48 hours.</p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="full_name" className="block text-sm font-medium text-[#0D4A2E] mb-2">Full Name *</label>
                    <input
                      id="full_name"
                      name="full_name"
                      data-testid="contact-input-name"
                      value={formData.full_name}
                      onChange={handleChange}
                      required
                      placeholder="Your name"
                      className="w-full px-4 py-3 border border-[#E5E5E5] focus:border-[#0D4A2E] focus:outline-none transition-colors duration-200"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-[#0D4A2E] mb-2">Email *</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      data-testid="contact-input-email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="Your email"
                      className="w-full px-4 py-3 border border-[#E5E5E5] focus:border-[#0D4A2E] focus:outline-none transition-colors duration-200"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-[#0D4A2E] mb-2">Subject *</label>
                  <input
                    id="subject"
                    name="subject"
                    data-testid="contact-input-subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    placeholder="What is this regarding?"
                    className="w-full px-4 py-3 border border-[#E5E5E5] focus:border-[#0D4A2E] focus:outline-none transition-colors duration-200"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-[#0D4A2E] mb-2">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    data-testid="contact-input-message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Your message..."
                    rows={6}
                    className="w-full px-4 py-3 border border-[#E5E5E5] focus:border-[#0D4A2E] focus:outline-none resize-none transition-colors duration-200"
                  />
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={isSubmitting}
                  className="inline-flex items-center gap-2 bg-[#C5A028] text-[#1A1A1A] px-8 py-4 font-bold hover:bg-[#D4B43D] transition-colors duration-200 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      SEND MESSAGE
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Map Placeholder */}
            <div className="bg-[#F5F5F5] flex items-center justify-center min-h-[400px]">
              <div className="text-center p-8">
                <MapPin className="w-16 h-16 text-[#C5A028] mx-auto mb-6" />
                <h3 className="text-2xl font-bold text-[#0D4A2E] mb-2">Visit Our Campus</h3>
                <p className="text-[#666666] mb-2">CEPRES International University</p>
                <p className="text-[#666666] mb-2">Gbarnga City, Bong County</p>
                <p className="text-[#666666] mb-6">Liberia, West Africa</p>
                <a
                  href="https://maps.google.com/?q=Gbarnga,Liberia"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="get-directions-btn"
                  className="inline-flex items-center gap-2 bg-[#0D4A2E] text-white px-6 py-3 font-semibold hover:bg-[#082D1C] transition-colors duration-200"
                >
                  <ArrowRight className="w-4 h-4" />
                  Get Directions
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
